import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useAccessToken } from "@/hooks";
import { QueryKeys } from "@/lib/types";
import { fetchAttendanceLogsForStudents } from "@/lib/services/attendanceService";
import type { GetMakeupResponse } from "@/openapi/generated";
import {
  applyMissedSessionDates,
  buildAttendanceLogDateMap,
  isPlaceholderMakeupDate,
  makeupItemsNeedMissedDateHydration,
} from "../shared/makeupDates";

function uniqueStudentIds(items: GetMakeupResponse[]): number[] {
  const ids = new Set<number>();
  for (const item of items) {
    const id = Number(item.student?.id);
    if (Number.isFinite(id) && id > 0) ids.add(id);
  }
  return [...ids].sort((a, b) => a - b);
}

function missedDateLookbackRange(): { fromDate: Date; toDate: Date } {
  const toDate = new Date();
  toDate.setHours(12, 0, 0, 0);
  toDate.setMonth(toDate.getMonth() + 1);
  const fromDate = new Date(toDate);
  fromDate.setFullYear(fromDate.getFullYear() - 3);
  return { fromDate, toDate };
}

export function makeupMissedSessionDatesQueryKey(studentIdsKey: string) {
  return [QueryKeys.Attendance, "makeup-missed-session-dates", studentIdsKey] as const;
}

export function useMakeupMissedSessionDates(
  items: GetMakeupResponse[],
  seedDates?: ReadonlyMap<string, Date | string>,
) {
  const accessToken = useAccessToken();
  const needsHydration = makeupItemsNeedMissedDateHydration(items);

  const missingStudentIds = useMemo(() => {
    if (!needsHydration) return [];
    const unresolved = items.filter((item) => {
      if (!isPlaceholderMakeupDate(item.sourceSessionDate)) return false;
      const logId = item.attendanceLogId?.trim();
      if (!logId) return false;
      if (seedDates?.has(logId)) return false;
      return true;
    });
    return uniqueStudentIds(unresolved);
  }, [items, needsHydration, seedDates]);

  const studentIdsKey = missingStudentIds.join(",");

  const { data: fetchedMap } = useQuery({
    queryKey: makeupMissedSessionDatesQueryKey(studentIdsKey),
    queryFn: async () => {
      if (!accessToken || missingStudentIds.length === 0) {
        return new Map<string, Date | string>();
      }
      const { fromDate, toDate } = missedDateLookbackRange();
      const logs = await fetchAttendanceLogsForStudents(
        accessToken,
        missingStudentIds,
        fromDate,
        toDate,
      );
      return buildAttendanceLogDateMap(logs);
    },
    enabled: Boolean(accessToken) && missingStudentIds.length > 0,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const dateByAttendanceLogId = useMemo(() => {
    const merged = new Map<string, Date | string>();
    if (seedDates) {
      for (const [id, date] of seedDates) merged.set(id, date);
    }
    if (fetchedMap) {
      for (const [id, date] of fetchedMap) merged.set(id, date);
    }
    return merged;
  }, [fetchedMap, seedDates]);

  const displayItems = useMemo(
    () => applyMissedSessionDates(items, dateByAttendanceLogId),
    [dateByAttendanceLogId, items],
  );

  return { displayItems, dateByAttendanceLogId };
}
